import React, { FC } from 'react';

import { Box } from '@mui/system';
import { Typography } from '@mui/material';

import { ProfessionalReplacementTypes } from '@pharmaplan/common';
import {
  ChipType,
  ReplacementLabels,
  SkeletonVariants,
} from '../../../helpers/Constants';
import useSelfService from '../../../hooks/useSelfService';
import ReplacementTag from '../ReplacementTag';
import strings from '../../../localization';
import WithSkeleton from '../WithSkeleton';

import HourlyRateChipLabel from './HourlyRateChipLabel';
import styles from './styles';

interface IAdminPharmacyFooter {
  software?: string;
  successAction: string;
  pharmacyHourlyRate?: number;
  pharmacistHourlyRate?: number;
  replacementType?: ProfessionalReplacementTypes;
  customHourlyLabel?: string;
  displayReplacement?: boolean;
  showPharmacyHourlyRate?: boolean;
  customPharmacistChip?: React.ReactNode | null;
}

const { pharmacyHourlyRate: pharmacyHourlyChip, hourlyRate } = ChipType;

const AdminPharmacyFooter: FC<IAdminPharmacyFooter> = ({
  software,
  pharmacyHourlyRate,
  pharmacistHourlyRate,
  successAction,
  replacementType,
  customHourlyLabel,
  displayReplacement,
  showPharmacyHourlyRate = true,
  customPharmacistChip,
}) => {
  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: SkeletonVariants.text,
  })(Typography);

  const { isSelfService } = useSelfService();
  let label = strings.pharmacistHourlyRate;
  const isReplacementDefined = replacementType !== undefined;

  if (isSelfService || displayReplacement) {
    label = strings.formatString(
      strings.hourlyRateFormat,
      ReplacementLabels[
        replacementType ?? ProfessionalReplacementTypes.pharmacist
      ],
    ) as string;
  }

  return (
    <Box sx={styles.mainContainer}>
      {!isSelfService && showPharmacyHourlyRate && (
        <HourlyRateChipLabel
          type={pharmacyHourlyChip}
          label={strings.pharmacyHourlyRate}
          rate={pharmacyHourlyRate}
          successAction={successAction}
        />
      )}
      {customPharmacistChip
        ?? (!!pharmacistHourlyRate || !software ? (
          <HourlyRateChipLabel
            type={hourlyRate}
            label={customHourlyLabel || label}
            rate={pharmacistHourlyRate}
            successAction={successAction}
          />
        ) : (
          <Box sx={styles.itemContainer}>
            <SkeletonTypography sx={styles.title}>
              {strings.software}
            </SkeletonTypography>
            <SkeletonTypography sx={styles.value}>
              {software}
            </SkeletonTypography>
          </Box>
        ))}
      {isReplacementDefined && (
        <Box sx={[styles.itemContainer]}>
          <ReplacementTag
            replacementType={replacementType}
            successAction={successAction}
          />
        </Box>
      )}
    </Box>
  );
};

export default AdminPharmacyFooter;
